/**
 * Accept a Squarespace URL, hostname or bare subdomain and return the lowercase
 * subdomain.
 *
 * The subdomain is what ecom-connector matches a merchant on for their first connect,
 * because it is the only identifier a merchant can actually see. Squarespace's own
 * website id never appears in their admin. Once connected, the connector rewrites
 * externalPlatformId to that stable id, so a later rename cannot orphan them.
 *
 * A custom domain is rejected rather than accepted, because Squarespace reports the
 * built-in `*.squarespace.com` subdomain regardless of what a site is served on, so a
 * custom domain here would never match anything.
 */
export const normalizeSquarespaceExternalId = (raw: string): string | null => {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }

  let host = trimmed;
  try {
    if (trimmed.includes('://')) {
      host = new URL(trimmed).hostname;
    } else {
      host = trimmed.split('/')[0]?.split('?')[0] ?? '';
    }
  } catch {
    return null;
  }

  if (!host) {
    return null;
  }

  if (host.endsWith('.squarespace.com')) {
    const subdomain = host.slice(0, -'.squarespace.com'.length);
    return subdomain.includes('.') || !subdomain ? null : subdomain;
  }

  // No dot at all means they typed the subdomain on its own, which is what we want.
  // Anything else is a custom domain and will never match.
  return host.includes('.') ? null : host;
};
